import { useCallback, useEffect } from "react";
import { ASC } from "@/constants/const";
import { useShowToast } from "@/v2/hooks";
import type { FilterItem } from "../types";

interface UseDashboardStateParams {
  setSearchValue: (value: string) => void;
  setFilterValue: (value: FilterItem[] | null) => void;
  setOrder: (value: string) => void;
  setOrderBy: (value: string) => void;
  setCurrentPageValue: (value: number) => void;
  errorMessage: string | null;
  clearErrorMessage: () => void;
  savedCity: string | null;
}

interface UseDashboardStateResult {
  refreshProps: () => void;
}

export function useDashboardState({
  setSearchValue,
  setFilterValue,
  setOrder,
  setOrderBy,
  setCurrentPageValue,
  errorMessage,
  clearErrorMessage,
  savedCity,
}: UseDashboardStateParams): UseDashboardStateResult {
  const showToast = useShowToast();

  // Reset all filter/search/pagination state
  const refreshProps = useCallback(() => {
    setSearchValue("");
    setFilterValue(null);
    setOrder(ASC);
    setOrderBy("");
    setCurrentPageValue(0);
  }, [
    setSearchValue,
    setFilterValue,
    setOrder,
    setOrderBy,
    setCurrentPageValue,
  ]);

  // Show error toast when errorMessage changes
  useEffect(() => {
    if (errorMessage !== null) {
      showToast(errorMessage, "error");
      clearErrorMessage();
    }
  }, [errorMessage, clearErrorMessage, showToast]);

  // Clear filters when city changes
  useEffect(() => {
    setFilterValue(null);
  }, [savedCity, setFilterValue]);

  return {
    refreshProps,
  };
}
